import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { type FolderIndexStatus } from '@/lib/api/indexedFoldersApi'
import { useTranslation } from 'react-i18next'
import { Folder, FileText, Clock, RefreshCw } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'

interface FolderIndexStatusPanelProps {
  status: FolderIndexStatus
  folderName: string
  progressPercent?: number
  onRefresh?: () => void
  onReindex?: () => void
  isRefreshing?: boolean
  isReindexing?: boolean
}

export function FolderIndexStatusPanel({
  status,
  folderName,
  progressPercent,
  onRefresh,
  onReindex,
  isRefreshing = false,
  isReindexing = false,
}: FolderIndexStatusPanelProps) {
  const { t } = useTranslation()

  const getStatusBadge = () => {
    switch (status.status) {
      case 'indexed':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            {t('planvorming.indexStatus.indexed')}
          </Badge>
        )
      case 'indexing':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            {t('planvorming.indexStatus.indexing')}
          </Badge>
        )
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            {t('planvorming.indexStatus.pending')}
          </Badge>
        )
      case 'error':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">{t('planvorming.indexStatus.error')}</Badge>
        )
      default:
        return <Badge variant="secondary">{t('planvorming.indexStatus.unknown')}</Badge>
    }
  }

  const lastIndexedDate = (() => {
    if (!status.lastIndexed) {
      return t('common.never')
    }

    try {
      return format(parseISO(status.lastIndexed), 'PPp', { locale: nl })
    } catch {
      return t('common.never')
    }
  })()

  const normalizedProgress = Math.max(10, Math.min(progressPercent ?? 45, 100))
  const hasActions = Boolean(onRefresh || onReindex)

  return (
    <Card className="p-4 hover:shadow-md transition-shadow h-full">
      <div className="space-y-4 h-full">
        {/* Header with name and status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <Folder className="w-4 h-4 text-blue-600 mt-1 shrink-0" />
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-sm truncate">{folderName}</h4>
            </div>
          </div>
          {getStatusBadge()}
        </div>

        {/* Breakdown columns */}
        <div className="rounded-md border overflow-hidden">
          <div className="grid grid-cols-2 bg-muted/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <span>{t('planvorming.statusPanel.component')}</span>
            <span className="text-right">{t('planvorming.statusPanel.value')}</span>
          </div>

          <div className="space-y-0">
            <div className="grid grid-cols-2 items-center px-3 py-2 text-sm border-t">
              <span className="flex items-center gap-2 text-muted-foreground">
                <FileText className="w-4 h-4" />
                {t('planvorming.documentCount')}
              </span>
              <span className="text-right font-medium">{status.documentCount ?? 0}</span>
            </div>
            <div className="grid grid-cols-2 items-center px-3 py-2 text-sm border-t">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Folder className="w-4 h-4" />
                {t('planvorming.folderCount')}
              </span>
              <span className="text-right font-medium">{status.folderCount ?? 0}</span>
            </div>
            <div className="grid grid-cols-2 items-center px-3 py-2 text-sm border-t">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                {t('planvorming.lastIndexed')}
              </span>
              <span className="text-right text-xs text-muted-foreground">{lastIndexedDate}</span>
            </div>
          </div>
        </div>

        {/* Progress bar if indexing */}
        {status.status === 'indexing' && (
          <div className="space-y-2">
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-full rounded-full transition-[width] duration-500 ease-out"
                style={{ width: `${normalizedProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('planvorming.indexingProgress')} ({Math.round(normalizedProgress)}%)
            </p>
          </div>
        )}

        {/* Actions */}
        {hasActions && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={!onRefresh || isRefreshing || isReindexing}
              className="justify-start"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t('planvorming.statusPanel.refreshStatus')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReindex}
              disabled={!onReindex || isReindexing || isRefreshing}
              className="justify-start"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isReindexing ? 'animate-spin' : ''}`} />
              {t('planvorming.statusPanel.reindexFolder')}
            </Button>
          </div>
        )}

        {/* Error message */}
        {status.status === 'error' && status.errorMessage && (
          <div className="bg-destructive/10 text-destructive text-xs p-2 rounded">
            {status.errorMessage}
          </div>
        )}
      </div>
    </Card>
  )
}
